import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid,
  Input,
  Button,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AxiosInstance from "../../AxiosInstance";
import "./style.css";
import { JobberAvatar } from "../../../components/MuiTable";
import { LoaderComponent } from "../../../components/Icon/Index";
import InputText from "../../../components/InputFields/InputText";
import Address from "../../../components/Address";
import { formatUSPhone } from "../../../utils/usFormat";
import { useUserType } from "../../../contexts/UserTypeContext";
import { handleAuth } from "../../../components/Login/Auth";
import { getItem, setItem, removeItem } from "../../../utils/storage";
import showToast from "../../../components/Toast/Toster";

const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/[^\d]/g, "");
  const limitedPhoneNumber = phoneNumber.slice(0, 10);
  const match = limitedPhoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (match) {
    let formattedNumber = "";
    if (match[1].length >= 3) {
      formattedNumber = `(${match[1]}) `;
    } else {
      formattedNumber = match[1];
    }
    formattedNumber += match[2];
    if (match[3]) {
      formattedNumber += `-${match[3]}`;
    }
    return formattedNumber;
  }
  return value;
};

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
const CustomerModal = ({
  isCustomer,
  setIsCustomer,
  isProperty,
  setIsProperty,
  setFieldValue,
  values,
  lineItems,
  setPropertyData,
  setCustomersData,
  formik,
  source,
  tokenDecode,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [customerData, setCustomerData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [loader, setLoader] = useState(true);
  const [CompanyId, setCompanyId] = useState();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  
  // Ref to track if we've processed the customer data from navigation
  const processedCustomerRef = useRef(null);
  // Ref to track previous isProperty value to detect when switching to property view
  const prevIsPropertyRef = useRef(isProperty);

  // New customer form states
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    FirstName: "",
    LastName: "",
    EmailAddress: "",
    PhoneNumber: "",
    Address: "",
    City: "",
    State: "",
    Zip: "",
    Country: "",
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerFormSubmitAttempted, setCustomerFormSubmitAttempted] = useState(false);
  const [newCustomerTouched, setNewCustomerTouched] = useState({
    FirstName: false,
    LastName: false,
    EmailAddress: false,
  });

  const customerAddressFormik = useFormik({
    initialValues: {
      Address: newCustomerData.Address || "",
      City: newCustomerData.City || "",
      State: newCustomerData.State || "",
      Zip: newCustomerData.Zip || "",
      Country: newCustomerData.Country || "",
    },
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (customerAddressFormik.values.Address !== undefined) {
      setNewCustomerData((prev) => ({
        ...prev,
        Address: customerAddressFormik.values.Address || "",
        City: customerAddressFormik.values.City || "",
        State: customerAddressFormik.values.State || "",
        Zip: customerAddressFormik.values.Zip || "",
        Country: customerAddressFormik.values.Country || "",
      }));
    }
  }, [
    customerAddressFormik.values.Address,
    customerAddressFormik.values.City,
    customerAddressFormik.values.State,
    customerAddressFormik.values.Zip,
    customerAddressFormik.values.Country,
  ]);

  const fetchTokenData = async () => {
    if (!CompanyId) {
      try {
        const token =
          getItem("adminToken") ||
          getItem("workerToken");

        if (!token) {
          console.error("Token not found in localStorage");
          return;
        }

        const res = await AxiosInstance.post(`/v1/auth/token_data`, { token });
        if (res?.data?.data?.CompanyId) {
          setCompanyId(res?.data?.data?.CompanyId);
        }
      } catch (error) {
        console.error("Error:", error?.message);
      } finally {
        setLoader(false);
      }
    }
  };

  useEffect(() => {
    fetchTokenData();
  }, []);

  const fetchData = async (showListLoader = true) => {
    try {
      if (showListLoader) setLoader(true);

      const res = await AxiosInstance.get(
        `/v1/customer/get_customer/${CompanyId}`
      );
      if (res?.data?.statusCode === 200) {
        setCustomerData(res?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error?.message);
    } finally {
      if (showListLoader) setLoader(false);
    }
  };

  useEffect(() => {
    if (CompanyId) {
      fetchData();
    }
  }, [CompanyId]);

  useEffect(() => {
    const cid = formik?.values?.CustomerId;
    const lid = formik?.values?.LocationId;
    if (!cid || !lid || customerData.length === 0) return;

    const data = customerData.find(
      (item) =>
        item?.CustomerId === cid ||
        item?.UserId === cid ||
        String(item?.CustomerId) === String(cid) ||
        String(item?.UserId) === String(cid)
    );
    // Do not overwrite a valid in-memory selection (e.g. just-created customer)
    // while the list is still refreshing or IDs use UserId vs CustomerId.
    if (!data) return;

    setCustomersData(data);
    const locationItem = data?.location?.find(
      (loc) =>
        loc?.LocationId === lid || String(loc?.LocationId) === String(lid)
    );
    if (locationItem) setPropertyData(locationItem);
  }, [formik?.values?.CustomerId, formik?.values?.LocationId, customerData]);

  // Clear search input when switching from customer to property selection mode
  useEffect(() => {
    // Only clear if switching from customer view (isProperty was false) to property view (isProperty is now true)
    if (isProperty && !prevIsPropertyRef.current && searchInput) {
      setSearchInput("");
    }
    // Update the ref to track current state
    prevIsPropertyRef.current = isProperty;
  }, [isProperty, searchInput]);

  const filteredCustomers = !isProperty
    ? customerData
        .filter((customer) => {
          // Only show active customers
          if (customer?.IsActive === false) {
            return false;
          }
          // Apply name format logic
          const firstName = customer?.FirstName || "";
          const lastName = customer?.LastName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          return fullName.toLowerCase().includes(searchInput?.toLowerCase());
        })
        .sort((a, b) => {
          // First sort by createdAt (newest first)
          const dateA = new Date(a?.createdAt || 0).getTime();
          const dateB = new Date(b?.createdAt || 0).getTime();
          
          if (dateB !== dateA) {
            return dateB - dateA; // Descending order (newest first)
          }
          
          // If dates are equal, sort alphabetically by name
          const firstNameA = a?.FirstName || "";
          const lastNameA = a?.LastName || "";
          const nameA = `${firstNameA} ${lastNameA}`.trim().toLowerCase();
          
          const firstNameB = b?.FirstName || "";
          const lastNameB = b?.LastName || "";
          const nameB = `${firstNameB} ${lastNameB}`.trim().toLowerCase();
          
          return nameA.localeCompare(nameB);
        })
    : locationData
        .filter((location) =>
          `${location?.Address || ""} ${location?.City || ""} ${
            location?.State || ""
          }`
            .toLowerCase()
            .includes(searchInput?.toLowerCase())
        )
        .sort((a, b) => {
          const addressA = `${a?.Address || ""} ${a?.City || ""} ${a?.State || ""}`.trim().toLowerCase();
          const addressB = `${b?.Address || ""} ${b?.City || ""} ${b?.State || ""}`.trim().toLowerCase();
          return addressA.localeCompare(addressB);
        });

  // Restore form data from localStorage only once on mount
  useEffect(() => {
    const savedData = localStorage.getItem("formData");
    if (savedData && formik && typeof formik.setValues === 'function') {
      // Only restore if we don't have customer data from navigation
      if (!location?.state?.Customer) {
        formik.setValues(JSON.parse(savedData));
      }
      // Clear the saved data after restoring to prevent re-processing
      localStorage.removeItem("formData"); // formData is not auth-related, keep in localStorage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    const updateData = () => {
      const customer = location?.state?.Customer;
      const customerId = location?.state?.UserId;
      
      // Skip if no customer data or if we've already processed this exact customer
      if (!customer) return;
      
      // Create a unique identifier for this customer data
      const customerIdentifier = `${customerId}-${customer?.CustomerId || ''}-${Date.now()}`;
      
      // Check if we've already processed this customer
      if (processedCustomerRef.current === customerId) {
        return;
      }
      
      // Mark this customer as processed
      processedCustomerRef.current = customerId;
      
      setCustomersData(customer);
      setFieldValue("CustomerId", customerId || "");

      if (customer?.location?.length === 1) {
        setPropertyData(customer?.location[0]);
        setFieldValue("LocationId", customer?.location[0]?.LocationId || "");
        // Close the modal after setting the data
        setIsCustomer(false);
        setIsProperty(false);
      } else if (customer?.location?.length > 1) {
        setLocationData(customer?.location || []);
        setIsCustomer(true);
        setIsProperty(true);
        setSearchInput(""); // Clear search when switching to property selection
      } else {
        // Handle case where location data might be in different format
        const locData = customer?.locationDetails || customer?.locations || [];
        if (locData.length === 1) {
          setPropertyData(locData[0]);
          setFieldValue("LocationId", locData[0]?.LocationId || "");
          // Close the modal after setting the data
          setIsCustomer(false);
          setIsProperty(false);
        } else if (locData?.length > 1) {
          setLocationData(locData);
          setIsCustomer(true);
          setIsProperty(true);
          setSearchInput(""); // Clear search when switching to property selection
        }
      }
      
      // Clear the navigation state to prevent re-processing on subsequent renders
      // Use replaceState to update history without triggering a re-render
      if (window.history && window.history.replaceState) {
        const newState = { ...location.state };
        delete newState.Customer;
        delete newState.UserId;
        window.history.replaceState(newState, '');
      }
    };

    updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.Customer, location?.state?.UserId]);

  const handleClose = (id, locationId) => {
    const currentNavigats = location?.state?.navigats || [];
    if (tokenDecode.Role === "Worker") {
      navigate(`/${CompanyUrl}/staff-member/workerinvoicetable`, {
        state: {
          CustomerId: id,
          LocationId: locationId,
          navigats: [
            "/index",
            "/staff-member",
            "/worker-invoice",
            "/workerinvoicetable",
          ],
        },
      });
    } else {
      navigate(`/${CompanyUrl}/invoicetable`, {
        state: {
          CustomerId: id,
          LocationId: locationId,
          navigats: ["/index", "/invoice", "/invoicetable"],
        },
      });
    }
  };

  const handleCreateCustomer = async () => {
    // Validate required fields - only FirstName, LastName, and EmailAddress
    const first = (newCustomerData.FirstName || "").trim();
    const last = (newCustomerData.LastName || "").trim();
    const email = (newCustomerData.EmailAddress || "").trim();

    if (!first || !last || !email || !isValidEmail(email)) {
      setCustomerFormSubmitAttempted(true);
      return;
    }

    try {
      setCreatingCustomer(true);

      const customerPayload = {
        FirstName: first,
        LastName: last,
        EmailAddress: email,
        PhoneNumber: newCustomerData.PhoneNumber || "",
        CompanyId: CompanyId,
        Address: newCustomerData.Address || "",
        City: newCustomerData.City || "",
        State: newCustomerData.State || "",
        Zip: newCustomerData.Zip || "",
        Country: newCustomerData.Country || "",
        Role: isDonationMode ? "Donor" : "Customer",
        AddedAt: new Date(),
      };

      const response = await AxiosInstance.post(
        "/v1/user",
        customerPayload
      );

      if (response?.data?.statusCode === 200 || response?.data?.statusCode === "200" || response?.data?.statusCode === 201) {
        showToast.success(isDonationMode ? "Donor created successfully!" : "Customer created successfully!");

        // Reset form
        setNewCustomerData({
          FirstName: "",
          LastName: "",
          EmailAddress: "",
          PhoneNumber: "",
          Address: "",
          City: "",
          State: "",
          Zip: "",
          Country: "",
        });
        setCustomerFormSubmitAttempted(false);
        setNewCustomerTouched({
          FirstName: false,
          LastName: false,
          EmailAddress: false,
        });
        setShowNewCustomerForm(false);

        // Select the newly created customer
        const newCustomer = response?.data?.data;
        if (newCustomer) {
          // Create location data from the form
          const locationData = {
            Address: newCustomerData.Address,
            City: newCustomerData.City,
            State: newCustomerData.State,
            Zip: newCustomerData.Zip,
            Country: newCustomerData.Country,
            LocationId: newCustomer?.LocationId || null,
          };

          const apiLocs = newCustomer?.location;
          const resolvedLocations =
            Array.isArray(apiLocs) && apiLocs.length > 0 ? apiLocs : [locationData];

          const customerWithLocation = {
            ...newCustomer,
            FirstName: newCustomer.FirstName || newCustomerData.FirstName,
            LastName: newCustomer.LastName || newCustomerData.LastName,
            EmailAddress: newCustomer.EmailAddress || newCustomerData.EmailAddress,
            PhoneNumber: newCustomer.PhoneNumber || newCustomerData.PhoneNumber,
            location: resolvedLocations,
          };

          // Set customer and location data
          setCustomersData(customerWithLocation);
          setFieldValue("CustomerId", newCustomer?.CustomerId || newCustomer?.UserId || "");
          
          if (customerWithLocation?.location?.length === 1) {
            setFieldValue("LocationId", customerWithLocation.location[0]?.LocationId || "");
            setPropertyData(customerWithLocation.location[0]);
            setIsProperty(false);
            setIsCustomer(false);
            if (source === "Invoice") {
              handleClose(
                newCustomer?.CustomerId || newCustomer?.UserId,
                customerWithLocation.location[0]?.LocationId || ""
              );
            }
          } else if (customerWithLocation?.location?.length > 1) {
            setLocationData(
              customerWithLocation.location.map((location) => ({
                ...location,
                CustomerId: newCustomer?.CustomerId || newCustomer?.UserId,
              }))
            );
            setIsProperty(true);
          } else {
            // If no location, use the one we created
            setFieldValue("LocationId", locationData?.LocationId || "");
            setPropertyData(locationData);
            setIsProperty(false);
            setIsCustomer(false);
            if (source === "Invoice") {
              handleClose(
                newCustomer?.CustomerId || newCustomer?.UserId,
                locationData?.LocationId || ""
              );
            }
          }

          // Refresh list without toggling modal loader (avoids full-page quote skeleton / stuck loader)
          fetchData(false);
        }
      } else {
        showToast.error(response?.data?.message || (isDonationMode ? "Failed to create donor" : "Failed to create customer"));
      }
    } catch (error) {
      if (error?.response?.data?.status === 202 || error?.response?.data?.statusCode === 409) {
        showToast.warning(error?.response?.data?.message || "Email is already taken");
      } else if (error?.response?.data?.statusCode === 400) {
        showToast.warning(error?.response?.data?.message?.[0] || error?.response?.data?.message || "Validation error");
      } else {
        showToast.error(error?.response?.data?.message || (isDonationMode ? "Failed to create donor" : "Failed to create customer"));
      }
    } finally {
      setCreatingCustomer(false);
    }
  };

  const newCustEmailTrim = (newCustomerData.EmailAddress || "").trim();
  const newCustomerFirstNameError =
    (customerFormSubmitAttempted || newCustomerTouched.FirstName) &&
    !newCustomerData.FirstName?.trim();
  const newCustomerLastNameError =
    (customerFormSubmitAttempted || newCustomerTouched.LastName) &&
    !newCustomerData.LastName?.trim();
  const newCustomerEmailError =
    (customerFormSubmitAttempted || newCustomerTouched.EmailAddress) &&
    (!newCustEmailTrim || !isValidEmail(newCustEmailTrim));
  const showNewCustomerRedBoxesMessage =
    newCustomerFirstNameError ||
    newCustomerLastNameError ||
    newCustomerEmailError;

  return (
    <Dialog
      open={isCustomer}
      onClose={() => {
        setIsCustomer(false);
        setIsProperty(false);
        setShowNewCustomerForm(false);
        setSearchInput(""); // Clear search when closing modal
        setNewCustomerData({
          FirstName: "",
          LastName: "",
          EmailAddress: "",
          PhoneNumber: "",
          Address: "",
          City: "",
          State: "",
          Zip: "",
          Country: "",
        });
        setCustomerFormSubmitAttempted(false);
        setNewCustomerTouched({
          FirstName: false,
          LastName: false,
          EmailAddress: false,
        });
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          margin: { xs: "8px", sm: "16px", md: "24px" },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)", md: "600px" },
          maxWidth: { xs: "calc(100% - 16px)", sm: "600px" },
          maxHeight: { xs: "calc(100vh - 16px)", sm: "90vh" },
          height: { xs: "auto", sm: "auto" },
        },
      }}
    >
      <DialogTitle 
        className={`${
          isDonationMode ? "text-donor-color" : "text-blue-color"
        } SelectCutomerHead`}
        sx={{
          padding: { xs: "16px 12px", sm: "20px 16px", md: "20px" },
          "& .MuiTypography-root": {
            fontSize: { xs: "14px", sm: "16px" },
          },
        }}
      >
        {isDonationMode
          ? "Select Or Create A Donor"
          : "Select Or Create A Customer"}
        <Typography
          className={`${
            isDonationMode ? "text-donor-color" : "text-blue-color"
          } edit_customer_function`}
          sx={{ 
            fontSize: { xs: "14px", sm: "16px" },
            marginTop: { xs: "4px", sm: "8px" },
          }}
        >
          Which {isDonationMode ? "donor" : "customer"} would you like to create this for?
        </Typography>
      </DialogTitle>
      <DialogContent 
        className="selectCustomer_box"
        sx={{
          padding: { xs: "12px", sm: "16px", md: "24px" },
          paddingTop: { xs: "40px", sm: "24px" },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setIsCustomer(!isCustomer);
            setIsProperty(false);
            setShowNewCustomerForm(false);
            setSearchInput(""); // Clear search when closing modal
            setNewCustomerData({
              FirstName: "",
              LastName: "",
              EmailAddress: "",
              PhoneNumber: "",
              Address: "",
              City: "",
              State: "",
              Zip: "",
              Country: "",
            });
            setCustomerFormSubmitAttempted(false);
            setNewCustomerTouched({
              FirstName: false,
              LastName: false,
              EmailAddress: false,
            });
          }}
          sx={{
            position: "absolute",
            right: { xs: 4, sm: 8 },
            top: { xs: 4, sm: 8 },
            padding: { xs: "8px", sm: "12px" },
            color: isDonationMode
              ? "var(--bg-donor-color)"
              : "var(--text-blue-color)",
            "& .MuiSvgIcon-root": { color: "inherit" },
            "&:hover": {
              color: isDonationMode
                ? "var(--bg-donor-color)"
                : "var(--text-blue-color)",
              backgroundColor: isDonationMode
                ? "rgba(104, 178, 203, 0.12)"
                : "rgba(15, 43, 70, 0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
        </IconButton>

        <Box
          className="mx-3"
          sx={{
            border: "1px solid rgba(6, 49, 100, 80%)",
            borderRadius: "5px",
            margin: { xs: "0 8px", sm: "0 12px" },
          }}
        >
          {loader ? (
            <Grid className="d-flex flex-direction-row justify-content-center align-items-center p-5 m-5">
              <LoaderComponent variant="skeleton" loader={loader} height="50" width="50" />
            </Grid>
          ) : (
            <DialogContent
              sx={{
                padding: { xs: "12px 8px", sm: "16px", md: "24px" },
                "&.MuiDialogContent-root": {
                  paddingTop: { xs: "12px", sm: "16px" },
                },
              }}
            >
              <Grid
                className="mb-3 selectcreatebtns clientSerach_createNew"
                container
                spacing={{ xs: 1, sm: 0 }}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1, sm: "20px" },
                }}
              >
                <Grid
                  className="searchClients"
                  sx={{
                    order: { xs: 1, sm: 1 },
                    marginBottom: { xs: "12px", sm: 0 },
                    flex: { xs: "none", sm: "1 1 0" },
                    minWidth: { sm: 0 },
                  }}
                  size={{
                    xs: 12,
                    sm: "grow"
                  }}>
                  <Input
                    id="search"
                    name="search"
                    placeholder={
                      isDonationMode ? "Search Donors" : "Search Customer"
                    }
                    type="text"
                    disableUnderline
                    className="text-blue-color customerModelSearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{
                      fontSize: { xs: "13px", sm: "14px" },
                      height: { xs: "38px", sm: "40px" },
                      backgroundColor: "transparent",
                      width: "100%",
                      "& .MuiInput-input": {
                        backgroundColor: "transparent !important",
                      },
                      "&.MuiInput-root": {
                        backgroundColor: "transparent !important",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "transparent !important",
                      },
                      "& input": {
                        backgroundColor: "transparent !important",
                        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
                          {
                            transition: "background-color 9999s ease-out",
                            WebkitTextFillColor: "#0f2b46 !important",
                            boxShadow:
                              "0 0 0px 1000px transparent inset !important",
                            WebkitBoxShadow:
                              "0 0 0px 1000px transparent inset !important",
                          },
                      },
                    }}
                    inputProps={{
                      style: {
                        backgroundColor: "transparent !important",
                      },
                    }}
                  />
                </Grid>
                <Grid
                  className="selectCustomerMOdel"
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    justifyContent: "center",
                    alignItems: "center",
                    order: { xs: 3, sm: 2 },
                    flex: { sm: "0 0 auto" },
                    maxWidth: { sm: "none" },
                  }}
                  size={{
                    xs: 12,
                    sm: 2
                  }}>
                  <Typography
                    className="selectCustomerOr text-blue-color"
                    sx={{ 
                      textAlign: "center",
                      fontSize: { xs: "12px", sm: "14px" },
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    or
                  </Typography>
                </Grid>
                <Grid
                  className={`btn ${
                    isDonationMode ? "bg-donor-color" : "bg-button-blue-color"
                  } text-white-color cratenclientmodal createNewCutomer`}
                  sx={{
                    minWidth: { xs: "0", sm: 0 },
                    flex: { xs: "none", sm: "1 1 0" },
                    fontSize: { xs: "13px", sm: "14px" },
                    height: { xs: "38px", sm: "40px" },
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    order: { xs: 2, sm: 3 },
                    marginBottom: { xs: 0, sm: 0 },
                    cursor: "pointer",
                    borderRadius: "4px",
                  }}
                  onClick={() => {
                    setShowNewCustomerForm(true);
                    setNewCustomerTouched({
                      FirstName: false,
                      LastName: false,
                      EmailAddress: false,
                    });
                    setCustomerFormSubmitAttempted(false);
                  }}
                  size={{
                    xs: 12,
                    sm: "grow"
                  }}>
                  {isDonationMode ? "Create new Donor" : "Create New Customer"}
                </Grid>
              </Grid>

              {showNewCustomerForm ? (
                <Box
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    p: { xs: 2, sm: 3 },
                    borderRadius: "8px",
                    backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.03)" : "rgba(6, 49, 100, 0.03)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: 600,
                      color: isDonationMode ? "var(--border-donor-color)" : "#0f2b46",
                      mb: { xs: 1.5, sm: 2 },
                    }}
                  >
                    Create New {isDonationMode ? "Donor" : "Customer"}
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <InputText
                        label="First Name *"
                        name="newCustomerFirstName"
                        value={newCustomerData.FirstName}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, FirstName: e.target.value })}
                        onBlur={() =>
                          setNewCustomerTouched((prev) => ({
                            ...prev,
                            FirstName: true,
                          }))
                        }
                        placeholder="First Name"
                        error={newCustomerFirstNameError}
                        helperText={undefined}
                        showErrorOnly={true}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <InputText
                        label="Last Name *"
                        name="newCustomerLastName"
                        value={newCustomerData.LastName}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, LastName: e.target.value })}
                        onBlur={() =>
                          setNewCustomerTouched((prev) => ({
                            ...prev,
                            LastName: true,
                          }))
                        }
                        placeholder="Last Name"
                        error={newCustomerLastNameError}
                        helperText={undefined}
                        showErrorOnly={true}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <InputText
                        label="Email *"
                        name="newCustomerEmail"
                        value={newCustomerData.EmailAddress}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, EmailAddress: e.target.value })}
                        onBlur={() =>
                          setNewCustomerTouched((prev) => ({
                            ...prev,
                            EmailAddress: true,
                          }))
                        }
                        placeholder="email@example.com"
                        type="email"
                        error={newCustomerEmailError}
                        helperText={undefined}
                        showErrorOnly={true}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <InputText
                        label="Phone"
                        value={newCustomerData.PhoneNumber}
                        onChange={(e) => {
                          const formattedPhone = formatPhoneNumber(e.target.value);
                          setNewCustomerData({ ...newCustomerData, PhoneNumber: formattedPhone });
                        }}
                        placeholder="(555) 555-5555"
                        type="text"
                      />
                    </Grid>
                    <Grid size={12}>
                      <Address
                        handleChange={(e) => {
                          customerAddressFormik.handleChange(e);
                          const name = e?.target?.name;
                          const value = e?.target?.value;
                          if (name) setNewCustomerData((prev) => ({ ...prev, [name]: value }));
                        }}
                        formik={customerAddressFormik}
                        submitAttempted={customerFormSubmitAttempted}
                        addressFieldsOptional
                      />
                    </Grid>
                  </Grid>
                  {showNewCustomerRedBoxesMessage && (
                    <Typography
                      sx={{
                        color: "#dc3545",
                        fontSize: "14px",
                        fontWeight: 500,
                        textAlign: "center",
                        mt: 2,
                        mb: 0,
                        px: 1,
                      }}
                    >
                      Please correct the red boxes above
                    </Typography>
                  )}
                  <Box 
                    sx={{ 
                      display: "flex", 
                      gap: { xs: 1.5, sm: 2 }, 
                      mt: { xs: 2, sm: 2.5 }, 
                      justifyContent: "flex-end",
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      "& .MuiButton-root": {
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: "auto" },
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowNewCustomerForm(false);
                        setNewCustomerData({
                          FirstName: "",
                          LastName: "",
                          EmailAddress: "",
                          PhoneNumber: "",
                          Address: "",
                          City: "",
                          State: "",
                          Zip: "",
                          Country: "",
                        });
                        setCustomerFormSubmitAttempted(false);
                        setNewCustomerTouched({
                          FirstName: false,
                          LastName: false,
                          EmailAddress: false,
                        });
                      }}
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreateCustomer}
                      disabled={creatingCustomer}
                      sx={{
                        backgroundColor: isDonationMode ? "var(--border-donor-color)" : "#0f2b46",
                        textTransform: "none",
                        "&:hover": { backgroundColor: isDonationMode ? "#1a9bc7" : "#052a4d" },
                      }}
                    >
                      {creatingCustomer ? <CircularProgress size={20} color="inherit" /> : `Create ${isDonationMode ? "Donor" : "Customer"}`}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {filteredCustomers?.length === 0 && (
                    <Grid
                      className="text-center mt-3 text-blue-color"
                      sx={{ 
                        fontSize: { xs: "13px", sm: "14px" },
                        padding: { xs: "16px 8px", sm: "16px" },
                      }}
                    >
                      {isDonationMode ? "No donors found" : "No customers found"}
                    </Grid>
                  )}

                  {filteredCustomers?.length > 0 && (
                    <>
                      <Typography
                        className="mt-2 leadSelectCustomer"
                        sx={{
                          color: "#07CF10",
                          paddingLeft: { xs: "20px", sm: "30px" },
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: "600",
                          marginTop: { xs: "12px", sm: "16px" },
                        }}
                      >
                        Active
                      </Typography>
                      <Grid sx={{ margin: { xs: "0 -8px", sm: "0 -15px" }, marginTop: "-10px" }}>
                        <hr style={{ border: "1px solid #0f2b46CC" }} />
                      </Grid>
                    </>
                  )}

                  {!isProperty
                ? filteredCustomers.map((item, index) => {
                    const displayName = `${item?.FirstName || ""} ${item?.LastName || ""}`.trim();
                    return (
                    <Grid
                      className="w-100 mt-3 text-blue-color customerMOdelUserName"
                      key={item?.CustomerId ?? index}
                      sx={{
                        cursor: "pointer",
                        padding: { xs: "12px", sm: "14px 16px" },
                        marginTop: { xs: "10px", sm: "12px" },
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: { xs: "10px", sm: "14px" },
                      }}
                      onClick={() => {
                        if (item?.location?.length === 1) {
                          setFieldValue("CustomerId", item?.CustomerId || "");
                          setFieldValue(
                            "LocationId",
                            item?.location[0]?.LocationId || ""
                          );
                          setIsProperty(false);
                          setIsCustomer(false);
                          setSearchInput(""); // Clear search when closing modal
                          if (source === "Invoice") {
                            handleClose(
                              item?.CustomerId,
                              item?.location[0]?.LocationId || ""
                            );
                          }
                        } else {
                          setLocationData(
                            item?.location?.map((location) => ({
                              ...location,
                              CustomerId: item?.CustomerId,
                            }))
                          );
                          setFieldValue("CustomerId", item?.CustomerId || "");
                          setIsProperty(true);
                          setSearchInput(""); // Clear search when switching to property selection
                        }
                      }}
                    >
                      {/* JOB-3256: fixed-width avatar + flex:1 text column. Was 8%/92%
                          column widths which squished the avatar at narrow viewports. */}
                      <Grid
                        sx={{
                          flex: "0 0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <JobberAvatar
                          name={displayName || "?"}
                          colorSeed={item?.UserId ?? item?.CustomerId}
                          showName={false}
                          className="customerModelUserIcon"
                        />
                      </Grid>
                      <Grid
                        className="customerDetailAddress"
                        sx={{
                          flex: "1 1 auto",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          className="py-0 my-0"
                          sx={{
                            fontSize: { xs: "14px", sm: "15px" },
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: "var(--text-primary, #0f2b46)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {displayName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12.5px",
                            lineHeight: 1.4,
                            marginTop: "2px",
                            color: "var(--text-secondary, #6b7280)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item?.location?.length}{" "}
                          {item?.location?.length === 1
                            ? "Property"
                            : "Properties"}{" "}
                          {item?.PhoneNumber && `| ${formatUSPhone(item?.PhoneNumber)}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })
                : filteredCustomers.map((location, index) => (
                    <Grid
                      onClick={() => {
                        setFieldValue("LocationId", location?.LocationId || "");
                        setPropertyData(location);
                        setIsProperty(false);
                        setIsCustomer(false);
                        setSearchInput(""); // Clear search when closing modal
                        if (source === "Invoice") {
                          handleClose(
                            location?.CustomerId,
                            location?.LocationId
                          );
                        }
                      }}
                      className="py-2 text-blue-color border-blue-color"
                      sx={{
                        borderTop: index !== 0 ? "1px solid rgba(6, 49, 100, 0.2)" : undefined,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: { xs: "12px 8px", sm: "16px" },
                        cursor: "pointer",
                      }}
                      key={index}
                    >
                      <Grid 
                        className="d-flex align-items-center w-100 secondPropertyOpen"
                        sx={{
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: "8px", sm: "0" },
                        }}
                      >
                        <Grid
                          sx={{ 
                            width: { xs: "100%", sm: "8%" },
                            marginBottom: { xs: "8px", sm: "0" },
                            display: "flex",
                            alignItems: "center",
                          }}
                          className="imageOfProperty"
                        >
                          <DomainAddOutlinedIcon 
                            sx={{ 
                              fontSize: { xs: "20px", sm: "24px" },
                            }}
                          />
                        </Grid>
                        <Grid
                          className="imageOfProperty"
                          sx={{ 
                            fontSize: { xs: "12px", sm: "13px" },
                            width: { xs: "100%", sm: "92%" },
                            lineHeight: { xs: "1.6", sm: "1.5" },
                          }}
                        >
                          <span>{location?.Address || ""}</span>
                          {location?.City && <span>, {location?.City}</span>}
                          {location?.State && (
                            <>
                              <br />
                              <span>{location?.State}</span>
                            </>
                          )}
                        </Grid>
                      </Grid>
                      <ChevronRightIcon 
                        sx={{ 
                          color: "#958e8edd",
                          fontSize: { xs: "20px", sm: "24px" },
                          display: { xs: "none", sm: "block" },
                        }} 
                      />
                    </Grid>
                  ))}
                </>
              )}
            </DialogContent>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerModal;

